import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Hook to detect if the document is currently visible
 * Returns true when tab is active, false when hidden
 */
export const useDocumentVisibility = (): boolean => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

/**
 * Hook for polling that pauses when tab is hidden
 * @param callback - Function to call on each interval
 * @param interval - Interval in milliseconds
 * @param enabled - Whether polling should be active
 */
export const useVisibilityPolling = (
  callback: () => void | Promise<void>,
  interval: number,
  enabled = true
) => {
  const isVisible = useDocumentVisibility();
  const callbackRef = useRef(callback);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only poll when visible and enabled
    if (isVisible && enabled) {
      intervalRef.current = setInterval(() => {
        callbackRef.current();
      }, interval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, enabled, interval]);

  // Return visibility state for component to use
  return { isVisible };
};

export const useDebounce = (time: number, callback: any) => {
  const ref: any = React.useRef();

  React.useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = React.useMemo(() => {
    const func = () => {
      ref.current?.();
    };

    return debounce(func, time);
  }, [time]);

  return debouncedCallback;
};

/**
 * Hook to detect network status (online/offline)
 * Returns { isOnline, wasOffline } where wasOffline tracks if we've been offline since mount
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
};

/**
 * Hook that combines visibility and network status for smart polling
 * Only polls when tab is visible AND network is online
 */
export const useSmartPolling = (
  callback: () => void | Promise<void>,
  interval: number,
  enabled = true
) => {
  const isVisible = useDocumentVisibility();
  const { isOnline } = useNetworkStatus();
  const callbackRef = useRef(callback);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only poll when visible, online, and enabled
    if (isVisible && isOnline && enabled) {
      intervalRef.current = setInterval(() => {
        callbackRef.current();
      }, interval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, isOnline, enabled, interval]);

  return { isVisible, isOnline };
};
